import type {
    BrandInterface,
    ColorInterface,
    FillInterface,
    GradientInterface,
    PaletteInterface,
    SpecialGradientInterface,
    SurfaceInterface,
    TextInterface,
} from '../types';

const Text: TextInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.6)',
    x30: 'rgba(0, 0, 0, 0.45)',
    Black: 'rgba(0, 0, 0, 0.9)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
};

const Fill: FillInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.35)',
    x30: 'rgba(0, 0, 0, 0.2)',
    x40: 'rgba(0, 0, 0, 0.1)',
    x50: 'rgba(0, 0, 0, 0.05)',
    x55: 'rgba(0, 0, 0, 0.04)',
    x60: 'rgba(0, 0, 0, 0.03)',
    White: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(255, 255, 255, 1)',
};

const Surface: SurfaceInterface = {
    x10: 'rgba(255, 255, 255, 1)',
    x20: 'rgba(255, 255, 255, 0.96)',
    x30: 'rgba(255, 255, 255, 1)',
    x40: 'rgba(255, 255, 255, 1)',
    Inverse: 'rgba(26, 26, 26, 1)',
    Overlay: 'rgba(0, 0, 0, 0.4)',
    Foreground: 'rgba(255, 255, 255, 1)',
};

const Sell: ColorInterface = {
    x10: 'rgba(0, 0, 0, 0.9)',
    x20: 'rgba(0, 0, 0, 0.05)',
    x30: 'rgba(0, 0, 0, 0.1)',
    x40: 'rgba(0, 0, 0, 0.45)',
    x50: 'rgba(0, 0, 0, 0.6)',
};

const Purple: ColorInterface = {
    x10: 'rgba(111, 38, 255, 1)',
    x20: 'rgba(111, 38, 255, 0.08)',
    x30: 'rgba(111, 38, 255, 0.12)',
    x40: 'rgba(93, 32, 214, 1)',
    x50: 'rgba(67, 23, 153, 1)',
};

const Blue: ColorInterface = {
    x10: 'rgba(0, 149, 255, 1)',
    x20: 'rgba(0, 149, 255, 0.08)',
    x30: 'rgba(0, 149, 255, 0.12)',
    x40: 'rgba(0, 125, 214, 1)',
    x50: 'rgba(0, 89, 153, 1)',
};

const Teal: ColorInterface = {
    x10: 'rgba(0, 181, 217, 1)',
    x20: 'rgba(0, 181, 217, 0.08)',
    x30: 'rgba(0, 181, 217, 0.12)',
    x40: 'rgba(0, 152, 182, 1)',
    x50: 'rgba(0, 109, 130, 1)',
};

const Green: ColorInterface = {
    x10: 'rgba(10, 194, 133, 1)',
    x20: 'rgba(10, 194, 133, 0.08)',
    x30: 'rgba(10, 194, 133, 0.16)',
    x40: 'rgba(8, 163, 112, 1)',
    x50: 'rgba(6, 116, 80, 1)',
};
const Yellow: ColorInterface = {
    x10: 'rgba(255, 214, 0, 1)',
    x20: 'rgba(255, 214, 0, 0.08)',
    x30: 'rgba(255, 214, 0, 0.12)',
    x40: 'rgba(214, 180, 0, 1)',
    x50: 'rgba(153, 128, 0, 1)',
};
const Orange: ColorInterface = {
    x10: 'rgba(255, 106, 0, 1)',
    x20: 'rgba(255, 106, 0, 0.08)',
    x30: 'rgba(255, 106, 0, 0.12)',
    x40: 'rgba(214, 89, 0, 1)',
    x50: 'rgba(153, 64, 0, 1)',
};

const Red: ColorInterface = {
    x10: 'rgba(217, 0, 145, 1)',
    x20: 'rgba(217, 0, 145, 0.08)',
    x30: 'rgba(217, 0, 145, 0.12)',
    x40: 'rgba(182, 0, 122, 1)',
    x50: 'rgba(130, 0, 87, 1)',
};

const Critical: ColorInterface = Red;

const Yes: ColorInterface = Green;
const No: ColorInterface = Red;

const Gradient: GradientInterface = {
    x10: 'radial-gradient(100% 100% at 0% 100%, #00B5D9 0%, #265CFF 31.77%, #AA00FF 66.15%, #D90048 100%)',
};

const Special: SpecialGradientInterface = {
    Mint: '#00D991',
    Black: 'linear-gradient(135deg, #B2ADA1 0%, #A09C91 25%, #D1CEC7 50%, #8E8A81 75%, #B2ADA1 100%)',
    Gold: '#F2BA49',
    Silver: '#B8CBD9',
    Bronze: '#E5AA95',
    Platinum: 'linear-gradient(135deg, #D9D9D9 0%, #AEAEAE 25%, #E8E8E8 50%, #828282 75%, #D9D9D9 100%)',
    Diamond:
        'linear-gradient(135deg, #95E5E5 0%, #A1A1E5 11.11%, #E5ACE5 22.22%, #F2F2DA 44.44%, #ACE5E5 55.56%, #7478A6 66.67%, #A1A1E5 77.78%, #D9D9C3 88.89%, #95E595 100%)',
    Brand: 'rgba(40, 204, 149, 1)',
};

const Brand: BrandInterface = {
    Primary: 'rgba(40, 204, 149, 1)',
    x20: 'rgba(229, 251, 244, 1)',
    x30: 'rgba(179, 244, 222, 1)',
    x40: 'rgba(128, 236, 200, 1)',
    x50: 'rgba(77, 228, 178, 1)',
    x60: 'rgba(0, 195, 130, 1)',
    x70: 'rgba(0, 152, 102, 1)',
    x80: 'rgba(0, 109, 73, 1)',
    Secondary: 'rgba(0, 65, 43, 1)',
    Tertiary: '#003221',
};

export const Palette_AccessibleLightMode: PaletteInterface = {
    Text,
    Fill,
    Surface,
    Yes,
    No,
    Sell,
    Critical,
    Purple,
    Blue,
    Teal,
    Green,
    Yellow,
    Orange,
    Red,
    Gradient,
    Special,
    Brand,
};
